import { Component, OnInit, OnChanges, Input, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LeadsService } from '../../../core/services/leads.service';
import { HistoryLogViewerComponent } from '../../../shared/components/history-log-viewer/history-log-viewer.component';
import { PaginatedAuditLog } from '../../../core/models/lead.model';

@Component({
  selector: 'app-leads-history-tab',
  standalone: true,
  imports: [CommonModule, HistoryLogViewerComponent],
  templateUrl: './leads-history-tab.component.html',
  styleUrl: './leads-history-tab.component.scss',
  host: {
    'style': 'display: flex; width: 100%; height: 100%;'
  }
})
export class LeadsHistoryTabComponent implements OnInit, OnChanges {
  @Input() leadId?: number | null;
  @Input() searchTerm: string = '';

  logs: PaginatedAuditLog["items"] = [];
  allLoadedItems: PaginatedAuditLog["items"] = [];
  
  currentPage = 1;
  pageSize = 25;
  hasNextPage = false;
  hasPreviousPage = false;
  
  loading = false;
  error: string | null = null;
  isLoadingMore = false;
  isSearching = false;
  
  isSearchExpanded = false; // No longer used since search bar is extracted
  
  sortOptions = {
    sortBy: 'eventatutc',
    sortOrder: 'desc' as 'asc' | 'desc'
  };

  constructor(private leadsService: LeadsService) {}

  ngOnInit(): void {
    if (this.leadId != null) {
      this.loadHistory();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['leadId'] && !changes['leadId'].firstChange && this.leadId != null) {
      this.loadHistory();
    }
    
    // Handle searchTerm changes from parent component
    if (changes['searchTerm']) {
      const newSearchTerm = changes['searchTerm'].currentValue || '';
      const previousSearchTerm = changes['searchTerm'].previousValue || '';
      
      // Only reload if search term actually changed
      if (newSearchTerm !== previousSearchTerm) {
        this.currentPage = 1;
        this.allLoadedItems = [];
        this.isSearching = newSearchTerm.trim().length > 0;
        this.error = null;
        this.loadHistory();
      }
    }
  }

  getSortIcon(): string {
    return this.sortOptions.sortOrder === 'desc' 
      ? 'assets/icons/sort-ascending.svg' 
      : 'assets/icons/sort-descending.svg';
  }

  getSortIconAlt(): string {
    return this.sortOptions.sortOrder === 'desc' 
      ? 'Sort newest first' 
      : 'Sort oldest first';
  }

  getSortAriaLabel(): string {
    return this.sortOptions.sortOrder === 'desc'
      ? 'Currently sorted newest first. Click to sort oldest first.'
      : 'Currently sorted oldest first. Click to sort newest first.';
  }

  // Search methods (deprecated - search bar is now in tabs component)
  toggleSearch(): void {
    // Deprecated - search bar is now in tabs component
  }

  onSearchBlur(): void {
    // Deprecated - search bar is now in tabs component
  }

  retryLoad(): void {
    this.error = null;
    this.loadHistory();
  }

  onSortOrderChange(order: 'asc' | 'desc'): void {
    this.sortOptions.sortOrder = order;
    this.currentPage = 1;
    this.allLoadedItems = [];
    this.loadHistory();
  }

  // Note: onSearchTermChange is no longer used since searchTerm is now an @Input
  // The parent component (leads-view) handles search changes and passes them down
  // This method is kept for backward compatibility but should not be called
  onSearchTermChange(term: string): void {
    // This method is deprecated - searchTerm is now managed by parent component
    // Changes are handled via ngOnChanges
  }

  onLoadMore(): void {
    if (!this.isLoadingMore && this.hasNextPage) {
      this.loadMoreHistory();
    }
  }

  // Load more data when scrolling
  private loadMoreHistory(): void {
    if (this.isLoadingMore || !this.hasNextPage) return;

    this.isLoadingMore = true;
    this.currentPage++;

    this.leadsService.getLeadHistoryPaginated(
      this.leadId!, 
      this.currentPage, 
      this.pageSize,
      this.searchTerm,
      this.sortOptions.sortBy,
      this.sortOptions.sortOrder
    ).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const newItems = response.data.items;
          this.allLoadedItems = [...this.allLoadedItems, ...newItems];
          this.logs = this.allLoadedItems;
          this.hasNextPage = response.data.hasNextPage;
          this.hasPreviousPage = response.data.hasPreviousPage;
        }
        this.isLoadingMore = false;
      },
      error: (error) => {
        console.error('📜 Error loading more:', error);
        this.isLoadingMore = false;
        this.currentPage--;
      }
    });
  }

  loadHistory(): void {
    if (!this.leadId) {
      this.error = 'No lead ID provided';
      return;
    }

    this.loading = true;
    this.error = null;

    this.leadsService.getLeadHistoryPaginated(
      this.leadId, 
      this.currentPage, 
      this.pageSize,
      this.searchTerm,
      this.sortOptions.sortBy,
      this.sortOptions.sortOrder
    ).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.allLoadedItems = response.data.items;
          this.logs = this.allLoadedItems;
          this.hasNextPage = response.data.hasNextPage;
          this.hasPreviousPage = response.data.hasPreviousPage;
        } else {
          this.error = 'Failed to load history';
          console.error(response.message);
          this.resetData();
        }
        this.loading = false;
        this.isSearching = false;
      },
      error: (error) => {
        console.error('Error loading lead history:', error);
        this.error = 'Failed to load history';
        this.loading = false;
        this.isSearching = false;
        this.resetData();
      }
    });
  }

  private resetData(): void {
    this.allLoadedItems = [];
    this.logs = [];
    this.hasNextPage = false;
    this.hasPreviousPage = false;
  }
}


